import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useFavorites } from "../lib/favorites";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Menu, X, LogOut, MessageSquare, Heart, Search, ChevronDown, Shield, Bell, LayoutDashboard, LayoutGrid, UserPlus, ScanLine, Briefcase, Share2 } from "lucide-react";
import Wordmark from "./Wordmark";
import NotificationsDropdown from "./NotificationsDropdown";
import QrScannerModal from "./QrScannerModal";
import { FEATURES } from "../lib/features";

const PRIMARY_ALL = [
  { to: "/marketplace", key: "nav.marketplace" },
  { to: "/rentals", key: "nav.rentals" },
  { to: "/auctions", key: "nav.auctions", feature: "AUCTIONS_ENABLED" },
  { to: "/financing", key: "nav.financing" },
  { to: "/insurance", key: "nav.insurance" },
];

const PRIMARY = PRIMARY_ALL.filter((item) => !item.feature || FEATURES[item.feature]);

const MORE = [
  { to: "/showrooms", key: "nav.showrooms" },
  { to: "/pre-purchase-inspection", key: "nav.prePurchaseInspection" },
  { to: "/periodic-inspection", key: "nav.periodicInspection" },
  { to: "/vehicle-history", key: "nav.vehicleHistory" },
  { to: "/ownership-transfer", key: "nav.ownershipTransfer" },
  { to: "/services", key: "nav.services" },
  { to: "/parts", key: "nav.parts" },
  { to: "/delivery", key: "nav.delivery" },
  { to: "/salvage", key: "nav.salvage" },
  { to: "/export", key: "nav.export" },
  { to: "/heavy", key: "nav.heavy" },
  { to: "/motorcycles", key: "nav.motorcycles" },
  { to: "/marine", key: "nav.marine" },
  { to: "/fleet", key: "fleet" },
  { to: "/trust", key: "nav.trust" },
  { to: "/disputes", key: "nav.dispute" },
  { to: "/pricing", key: "nav.pricing" },
  { to: "/contact", key: "nav.contact" },
];

export default function Header() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const { count: favCount } = useFavorites();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [q, setQ] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const fetchUnread = async () => {
      try {
        const { data } = await apiClient.get("/conversations/unread-count");
        if (alive) setUnread(data.count || 0);
      } catch {
        /* ignore */
      }
    };
    fetchUnread();
    // iter233 — poll every 10s (was 20s) + refetch immediately whenever
    // the tab regains focus or becomes visible again. This is the cheapest
    // way to keep the 💬 badge in sync without introducing WebSockets:
    // a user switching back from another tab, another app, or the phone's
    // home screen instantly sees fresh unread state.
    const tm = setInterval(fetchUnread, 10000);
    const onFocus = () => { fetchUnread(); };
    const onVis = () => { if (document.visibilityState === "visible") fetchUnread(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      clearInterval(tm);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  const submitSearch = (e) => {
    e.preventDefault();
    const raw = (q || "").trim();
    // ── Ad number shortcut: "CAR-00000024" or just "24" → jump straight to
    //    the listing detail page. Uses the dedicated backend lookup so users
    //    who receive an ad # via WhatsApp/print can find it in one keystroke.
    const digits = raw.replace(/\D+/g, "");
    const isAdNumberLike =
      /^car[-\s]?\d+$/i.test(raw) ||
      (/^\d+$/.test(raw) && digits.length >= 1 && digits.length <= 10);
    if (isAdNumberLike && digits) {
      (async () => {
        try {
          const { data } = await apiClient.get(`/vehicles/by-ad-number/CAR-${digits.padStart(8, "0")}`);
          if (data && data.id) {
            navigate(`/vehicle/${data.id}`);
            setOpen(false);
            setQ("");
            return;
          }
        } catch {
          /* fall through to marketplace search */
        }
        navigate(`/marketplace?q=${encodeURIComponent(raw)}`);
        setOpen(false);
      })();
      return;
    }
    navigate(`/marketplace?q=${encodeURIComponent(raw)}`);
    setOpen(false);
  };

  const handleQrResult = (text) => {
    setQrOpen(false);
    if (!text) return;
    try {
      // Prefer URLs — extract path and route via SPA navigate if same origin.
      const u = new URL(text);
      if (u.origin === window.location.origin) {
        navigate(u.pathname + u.search + u.hash);
        return;
      }
      // External URL from a CARTAL QR that points at a different origin
      // (e.g. cartal.sa scanned on preview) — extract /vehicle/{id} path.
      const m = u.pathname.match(/\/vehicle\/([\w-]+)/);
      if (m) { navigate(`/vehicle/${m[1]}`); return; }
      // Unknown but valid URL — open in new tab as fallback.
      window.open(text, "_blank", "noopener,noreferrer");
    } catch {
      // Not a URL — assume it's an ad number like "CAR-00000024"
      const digits = text.replace(/\D+/g, "");
      if (digits) {
        navigate(`/marketplace?q=${encodeURIComponent(text)}`);
      }
    }
  };

  return (
    <header
      className="sticky top-0 z-50 bg-[#fafaf7]/92 backdrop-blur-md border-b border-[#0a0a0a]/[0.08]"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 h-[64px] sm:h-[72px] flex items-center justify-between gap-3">
        {/* Logo — desktop: mark + wordmark; mobile: properly sized image */}
        <Link to="/" className="shrink-0" data-testid="logo-link" onClick={() => setOpen(false)}>
          <Wordmark size="md" variant="mark" className="hidden sm:inline-flex" />
          <img
            src="https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png"
            alt="CARTAL"
            className="sm:hidden h-9 w-auto"
          />
        </Link>

        {/* Header search */}
        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md mx-2" data-testid="header-search">
          <div className="relative w-full">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("home.searchPlaceholder")}
              className="input-dark ps-10 pe-10 text-sm bg-white"
              data-testid="header-search-input"
            />
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="absolute end-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] transition-colors shadow-sm"
              title={lang === "ar" ? "امسح رمز QR للإعلان" : "Scan an ad's QR code"}
              aria-label={lang === "ar" ? "امسح رمز QR" : "Scan QR"}
              data-testid="header-qr-scan"
            >
              <ScanLine className="w-4 h-4" />
            </button>
          </div>
        </form>

        {/* Primary nav */}
        <nav className="hidden xl:flex items-center gap-6 text-[13px] text-[#525252] shrink-0">
          {PRIMARY.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `hover:text-[#0a0a0a] transition-colors ${isActive ? "text-[#0a0a0a] font-medium" : ""}`}
              data-testid={`nav-${l.key}`}
            >
              {t(l.key)}
            </NavLink>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button className="inline-flex items-center gap-1 hover:text-[#0a0a0a]" type="button" data-testid="nav-more">
              {lang === "ar" ? "المزيد" : "More"} <ChevronDown className="w-3 h-3" />
            </button>
            {moreOpen && (
              <div className="absolute top-full end-0 pt-3 z-40">
                <div className="bg-white rounded-xl border border-[#0a0a0a]/[0.08] shadow-lg p-3 grid grid-cols-2 gap-1 w-[420px]">
                  {MORE.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      className="block px-3 py-2 text-sm text-[#525252] hover:text-[#0a0a0a] hover:bg-[#f5f4ef] rounded-lg"
                      data-testid={`nav-${l.key}`}
                    >
                      {t(l.key)}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Always-visible mobile CTA (< xl) — placed FIRST so in RTL it appears
              closest to the center of the header (comfortable thumb reach) rather
              than at the extreme edge where iOS gesture areas interfere. */}
          {user === undefined ? null : user ? (
            <Link
              to="/dashboard"
              className="xl:hidden inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[13px] font-semibold bg-[#0a0a0a] text-[#f7eecd] hover:bg-[#1a1a1a] shadow-sm"
              data-testid="mobile-cta-dashboard"
              aria-label="Dashboard"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">{t("nav.dashboard")}</span>
            </Link>
          ) : (
            <Link
              to="/join"
              className="xl:hidden inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[13px] font-semibold shadow-sm"
              style={{ background: "linear-gradient(135deg,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
              data-testid="mobile-cta-register"
            >
              <UserPlus className="w-4 h-4" />
              <span>{lang === "ar" ? "انضم" : "Join"}</span>
            </Link>
          )}

          {/* CARTAL Business — always visible, ties into new B2B ecosystem */}
          <Link
            to="/business"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-[12px] font-semibold border transition-colors"
            style={{
              borderColor: "rgba(201,165,92,0.55)",
              color: "#8a6f2e",
              background: "linear-gradient(135deg,rgba(232,200,115,0.10),rgba(201,165,92,0.04))",
            }}
            title={lang === "ar" ? "كرتال أعمال" : "CARTAL Business"}
            data-testid="nav-cartal-business"
          >
            <Briefcase className="w-4 h-4" />
            <span className="hidden md:inline">{lang === "ar" ? "كرتال أعمال" : "For Business"}</span>
          </Link>

          <button
            type="button"
            onClick={toggle}
            className="hidden sm:inline-flex text-[12px] tracking-widest text-[#525252] hover:text-[#0a0a0a] px-2 py-1"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            {lang === "ar" ? "EN" : "ع"}
          </button>

          {user === undefined ? (
            <div className="w-20 h-8 rounded-full bg-[#0a0a0a]/[0.06] animate-pulse" />
          ) : user ? (
            <div className="hidden xl:flex items-center gap-1">
              <Link to="/favorites" className="relative p-2 text-[#525252] hover:text-[#0a0a0a]" data-testid="nav-favorites" aria-label="Favorites">
                <Heart className={`w-[18px] h-[18px] ${favCount > 0 ? "text-[#a8893e] fill-current" : ""}`} />
                {favCount > 0 && (
                  <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-[16px] px-[3px] rounded-full bg-[#c9a55c] text-[#0a0a0a] text-[9px] font-bold flex items-center justify-center">{favCount}</span>
                )}
              </Link>
              <Link to="/messages" className="relative p-2 text-[#525252] hover:text-[#0a0a0a]" data-testid="nav-messages" aria-label="Messages">
                <MessageSquare className="w-[18px] h-[18px]" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-[16px] px-[3px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="nav-messages-badge">{unread > 99 ? "99+" : unread}</span>
                )}
              </Link>
              <NotificationsDropdown lang={lang} />
              <Link to="/dashboard" className="text-[13px] text-[#525252] hover:text-[#0a0a0a] px-3 py-2" data-testid="nav-dashboard">
                {t("nav.dashboard")}
              </Link>
              <Link to="/orders" className="text-[13px] text-[#525252] hover:text-[#0a0a0a] px-3 py-2" data-testid="nav-orders">
                {lang === "ar" ? "طلباتي" : "Orders"}
              </Link>
              <Link to="/advertise" className="text-[13px] text-[#525252] hover:text-[#0a0a0a] px-3 py-2" data-testid="nav-advertise">
                {lang === "ar" ? "أعلِن معنا" : "Advertise"}
              </Link>
              {["super_admin", "admin"].includes(user.role) && (
                <Link to="/admin" className="text-[13px] text-[#8a6f2e] hover:text-[#0a0a0a] px-3 py-2 font-medium inline-flex items-center gap-1" data-testid="nav-admin">
                  <Shield className="w-3.5 h-3.5" /> {lang === "ar" ? "الإدارة" : "Admin"}
                </Link>
              )}
              <Link
                to="/settings/account"
                className="text-[13px] text-[#525252] hover:text-[#0a0a0a] px-2 py-2"
                data-testid="nav-account-settings"
                title={lang === "ar" ? "إعدادات الحساب" : "Account settings"}
              >
                {lang === "ar" ? "حسابي" : "Account"}
              </Link>
              <button type="button" onClick={() => logout()} className="p-2 text-[#525252] hover:text-[#0a0a0a]" data-testid="nav-logout" aria-label="Logout">
                <LogOut className="w-[18px] h-[18px]" />
              </button>
            </div>
          ) : (
            <div className="hidden xl:flex items-center gap-2">
              <Link to="/advertise" className="text-[13px] text-[#525252] hover:text-[#0a0a0a] px-3 py-2" data-testid="nav-advertise-public">{lang === "ar" ? "أعلِن معنا" : "Advertise"}</Link>
              <Link to="/login" className="text-[13px] text-[#525252] hover:text-[#0a0a0a] px-3 py-2" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/join" className="btn-accent text-[13px]" data-testid="nav-register">{t("nav.register")}</Link>
            </div>
          )}

          {/* iter185 bug-fix: notification bell was previously hidden
              behind `xl:flex` so mobile / tablet users never saw it.
              Surface an always-visible bell on ALL viewports below XL
              for any signed-in user (individuals, dealers, admins).
              iter233 — the 💬 messages icon needs the same treatment
              (previously only rendered inside the XL desktop cluster
              above), so mobile/tablet users can see & tap the icon
              with its unread badge without opening the drawer. */}
          {user && (
            <div className="xl:hidden flex items-center">
              <Link
                to="/messages"
                className="relative p-2 text-[#525252] hover:text-[#0a0a0a]"
                data-testid="nav-messages-mobile"
                aria-label="Messages"
              >
                <MessageSquare className="w-[18px] h-[18px]" />
                {unread > 0 && (
                  <span
                    className="absolute -top-0.5 -end-0.5 min-w-[16px] h-[16px] px-[3px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
                    data-testid="nav-messages-mobile-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
              <NotificationsDropdown lang={lang} />
            </div>
          )}

          <button type="button" className="xl:hidden inline-flex items-center justify-center w-11 h-11 -me-1 text-[#0a0a0a] rounded-full hover:bg-[#0a0a0a]/[0.05]" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && typeof document !== "undefined" && createPortal(
        <>
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="xl:hidden fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm cartal-drawer-fade"
            data-testid="mobile-drawer-backdrop"
          />

          {/* Drawer — luxury dark theme matching Live Auctions aesthetic */}
          <div
            className="xl:hidden fixed z-[101] top-0 bottom-0 end-0 w-[92%] max-w-[420px] overflow-y-auto shadow-2xl cartal-drawer-slide"
            style={{
              background: "linear-gradient(160deg,#0a0a0a 0%,#141110 45%,#1f1614 100%)",
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            data-testid="mobile-drawer"
          >
            {/* Ambient glow orbs */}
            <div aria-hidden className="absolute inset-0 opacity-25 pointer-events-none overflow-hidden">
              <div className="absolute -top-32 -end-32 w-96 h-96 rounded-full blur-3xl" style={{ background: "#c9a55c" }} />
              <div className="absolute -bottom-32 -start-32 w-96 h-96 rounded-full blur-3xl" style={{ background: "#8a6f2e" }} />
            </div>

            <div className="relative">
              {/* Header row with close button + brand */}
              <div className="flex items-center justify-between px-5 pt-5 pb-4">
                <div className="font-display text-xl font-bold tracking-tight" style={{ color: "#f7eecd" }}>
                  CARTAL <span style={{ color: "#c9a55c" }}>·</span> <span className="text-[13px] font-medium" style={{ color: "rgba(247,238,205,0.55)" }}>{lang === "ar" ? "كارتال" : "Ivory"}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full text-[#f7eecd] hover:bg-white/10 transition-colors"
                  data-testid="mobile-drawer-close"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search */}
              <div className="px-5 pb-5">
                <form onSubmit={submitSearch}>
                  <div className="relative">
                    <Search className="absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c9a55c]" />
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder={t("home.searchPlaceholder")}
                      className="w-full h-11 rounded-full ps-11 pe-12 text-sm bg-white/[0.06] border border-white/10 text-[#f7eecd] placeholder:text-[#f7eecd]/40 focus:outline-none focus:border-[#c9a55c] focus:bg-white/[0.09] transition-all"
                      data-testid="mobile-search-input"
                    />
                    <button
                      type="button"
                      onClick={() => { setOpen(false); setQrOpen(true); }}
                      className="absolute end-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#c9a55c] text-[#0a0a0a] hover:bg-[#d4b06b] transition-colors"
                      title={lang === "ar" ? "امسح رمز QR" : "Scan QR"}
                      aria-label={lang === "ar" ? "امسح رمز QR" : "Scan QR"}
                      data-testid="mobile-qr-scan"
                    >
                      <ScanLine className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </div>

              {/* Account hero card */}
              <div className="px-5 pb-5">
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    background: "linear-gradient(135deg,rgba(201,165,92,0.12) 0%,rgba(138,111,46,0.06) 100%)",
                    borderColor: "rgba(201,165,92,0.25)",
                  }}
                >
                  {user ? (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-[#0a0a0a]" style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}>
                          {(user.name || user.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[#f7eecd] font-semibold truncate">{user.name || (lang === "ar" ? "مرحباً" : "Welcome")}</div>
                          <div className="text-[11px] text-[#f7eecd]/60 truncate">{user.email}</div>
                        </div>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setOpen(false)}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full h-11 text-sm font-bold shadow-lg hover:scale-[1.02] transition-transform"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                        data-testid="mobile-nav-dashboard"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {t("nav.dashboard")}
                      </Link>
                    </>
                  ) : (
                    <>
                      <div className="text-[#f7eecd] font-display text-lg font-bold mb-1">
                        {lang === "ar" ? "انضم إلى كارتال" : "Join CARTAL"}
                      </div>
                      <div className="text-[12px] text-[#f7eecd]/60 mb-4">
                        {lang === "ar" ? "احفظ سياراتك المفضلة وسجّل إعلاناتك." : "Save favorites & post listings."}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Link
                          to="/join"
                          onClick={() => setOpen(false)}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-full h-11 text-sm font-bold shadow-lg hover:scale-[1.02] transition-transform"
                          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                          data-testid="mobile-nav-register"
                        >
                          <UserPlus className="w-4 h-4" /> {t("nav.register")}
                        </Link>
                        <Link
                          to="/login"
                          onClick={() => setOpen(false)}
                          className="w-full inline-flex items-center justify-center rounded-full h-10 text-sm font-medium text-[#f7eecd]/85 border border-white/15 hover:bg-white/[0.05] transition-colors"
                          data-testid="mobile-nav-login"
                        >
                          {t("nav.login")}
                        </Link>
                      </div>
                    </>
                  )}
                </div>

                {/* CARTAL Business — B2B entry point (mobile drawer) */}
                <Link
                  to="/business"
                  onClick={() => setOpen(false)}
                  className="mt-3 w-full inline-flex items-center justify-between gap-3 rounded-2xl p-4 border transition-colors"
                  style={{
                    background: "linear-gradient(135deg,rgba(247,238,205,0.06),rgba(201,165,92,0.04))",
                    borderColor: "rgba(201,165,92,0.28)",
                  }}
                  data-testid="mobile-nav-cartal-business"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                    >
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[#f7eecd] font-bold text-[14px] truncate">
                        {lang === "ar" ? "كرتال أعمال" : "CARTAL Business"}
                      </div>
                      <div className="text-[11px] text-[#f7eecd]/60 truncate">
                        {lang === "ar" ? "منظومة B2B لقطاع المركبات" : "B2B for the mobility sector"}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#c9a55c] -rotate-90 rtl:rotate-90 shrink-0" />
                </Link>
              </div>

              {/* User actions grid (only if logged in) */}
              {user && (
                <div className="px-5 pb-5">
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { to: "/my-listings", icon: LayoutGrid, labelAr: "إعلاناتي", labelEn: "My ads", testid: "my-listings", badge: 0 },
                      { to: "/orders", icon: LayoutDashboard, labelAr: "طلباتي", labelEn: "Orders", testid: "orders", badge: 0 },
                      { to: "/messages", icon: MessageSquare, labelAr: "الرسائل", labelEn: "Messages", testid: "messages", badge: unread },
                      { to: "/favorites", icon: Heart, labelAr: "المفضلة", labelEn: "Favorites", testid: "favorites", badge: favCount },
                    ].map((a) => (
                      <Link
                        key={a.to}
                        to={a.to}
                        onClick={() => setOpen(false)}
                        className="relative flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] hover:border-[#c9a55c]/40 transition-all text-[#f7eecd]"
                        data-testid={`mobile-nav-${a.testid}`}
                      >
                        <a.icon className="w-4 h-4 text-[#c9a55c]" />
                        <span className="text-[11px] font-medium">{lang === "ar" ? a.labelAr : a.labelEn}</span>
                        {a.badge > 0 && (
                          <span className="absolute top-1.5 end-1.5 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center" style={{ background: "#dc2626", color: "#fff" }}>{a.badge}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Link
                      to="/advertise"
                      onClick={() => setOpen(false)}
                      className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] font-semibold text-[#f7eecd] border border-white/15 hover:bg-white/[0.05] transition-colors"
                      data-testid="mobile-nav-advertise"
                    >
                      {lang === "ar" ? "أعلِن معنا" : "Advertise"}
                    </Link>
                    {["super_admin", "admin"].includes(user.role) && (
                      <Link
                        to="/admin"
                        onClick={() => setOpen(false)}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] font-bold border transition-colors"
                        style={{ borderColor: "rgba(201,165,92,0.5)", color: "#e8c873", background: "rgba(201,165,92,0.08)" }}
                        data-testid="mobile-nav-admin"
                      >
                        <Shield className="w-3.5 h-3.5" /> {lang === "ar" ? "الإدارة" : "Admin"}
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Guest advertise (only if logged out) */}
              {!user && (
                <div className="px-5 pb-5">
                  <Link
                    to="/advertise"
                    onClick={() => setOpen(false)}
                    className="w-full inline-flex items-center justify-center py-2.5 rounded-full text-[12px] font-semibold text-[#f7eecd]/85 border border-white/15 hover:bg-white/[0.05] transition-colors"
                    data-testid="mobile-nav-advertise-public"
                  >
                    {lang === "ar" ? "أعلِن معنا" : "Advertise with us"}
                  </Link>
                </div>
              )}

              {/* Divider */}
              <div className="px-5">
                <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(201,165,92,0.35), transparent)" }} />
              </div>

              {/* Nav sections */}
              <div className="px-5 pt-5 pb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#c9a55c] mb-3">
                  {lang === "ar" ? "الأساسيات" : "Essentials"}
                </div>
                <div className="grid grid-cols-1 gap-0.5">
                  {PRIMARY.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) => `flex items-center justify-between py-3 px-3 rounded-lg text-[15px] transition-colors ${isActive ? "text-[#e8c873] bg-white/[0.04]" : "text-[#f7eecd] hover:bg-white/[0.04]"}`}
                      data-testid={`mobile-nav-${l.key}`}
                    >
                      <span>{t(l.key)}</span>
                      <span className="text-[#c9a55c]/50 rtl:rotate-180"><ChevronDown className="w-4 h-4 -rotate-90" /></span>
                    </NavLink>
                  ))}
                </div>
              </div>

              <div className="px-5 pt-2 pb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#c9a55c] mb-3">
                  {lang === "ar" ? "خدمات وتصفح" : "Services & More"}
                </div>
                <div className="grid grid-cols-2 gap-0.5">
                  {MORE.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) => `py-2.5 px-3 rounded-lg text-[13px] transition-colors ${isActive ? "text-[#e8c873] bg-white/[0.04]" : "text-[#f7eecd]/85 hover:text-[#f7eecd] hover:bg-white/[0.04]"}`}
                      data-testid={`mobile-nav-${l.key}`}
                    >
                      {t(l.key)}
                    </NavLink>
                  ))}
                </div>
                {/* iter171 — Share CARTAL: native Web Share API when
                    available, clipboard fallback otherwise. Zero visual
                    impact elsewhere; lives inside the existing menu. */}
                <button
                  type="button"
                  onClick={async () => {
                    const shareData = {
                      title: "CARTAL",
                      text: lang === "ar"
                        ? "اكتشف كارتال — منصة السيارات الرائدة في السعودية."
                        : "Discover CARTAL — Saudi Arabia's leading automotive marketplace.",
                      url: `https://cartal.sa/api/share/${lang}`,
                    };
                    try {
                      if (typeof navigator !== "undefined" && navigator.share) {
                        await navigator.share(shareData);
                        setOpen(false);
                        return;
                      }
                      // Fallback — copy the CARTAL link
                      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                        await navigator.clipboard.writeText(shareData.url);
                      } else {
                        const ta = document.createElement("textarea");
                        ta.value = shareData.url;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand("copy");
                        ta.remove();
                      }
                      toast.success(lang === "ar" ? "تم نسخ رابط كارتال" : "CARTAL link copied");
                      setOpen(false);
                    } catch (_e) {
                      // User cancelled / share aborted — swallow silently.
                    }
                  }}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[13px] font-semibold text-[#f7eecd] border border-white/15 hover:bg-white/[0.05] transition-colors"
                  data-testid="mobile-nav-share-cartal"
                >
                  <Share2 className="w-4 h-4" />
                  {lang === "ar" ? "شارك كارتال" : "Share CARTAL"}
                </button>
              </div>

              {/* Bottom bar: language + logout */}
              <div className="px-5 pt-4 pb-6 mt-2 border-t border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggle}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] font-semibold text-[#f7eecd] border border-white/15 hover:bg-white/[0.05] transition-colors"
                    data-testid="mobile-drawer-lang-toggle"
                  >
                    {lang === "ar" ? "English" : "العربية"}
                  </button>
                  {user && (
                    <Link
                      to="/settings/account"
                      onClick={() => setOpen(false)}
                      className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] font-semibold text-[#f7eecd] border border-white/15 hover:bg-white/[0.05] transition-colors"
                      data-testid="mobile-nav-account"
                    >
                      {lang === "ar" ? "حسابي" : "Account"}
                    </Link>
                  )}
                  {user && (
                    <button
                      type="button"
                      onClick={() => { logout(); setOpen(false); }}
                      className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] font-semibold text-[#fca5a5] border border-red-500/30 hover:bg-red-500/10 transition-colors"
                      data-testid="mobile-nav-logout"
                    >
                      <LogOut className="w-3.5 h-3.5" /> {t("nav.logout")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
      <QrScannerModal open={qrOpen} onClose={() => setQrOpen(false)} onResult={handleQrResult} />
    </header>
  );
}
